import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/lib/api'
import { isOnboardingTourMode, type OnboardingTourMode } from '#/lib/onboarding'

interface AppConfig {
  registrationEnabled: boolean
  onboardingTour: OnboardingTourMode
}

interface AppConfigContextValue extends AppConfig {
  isPending: boolean
}

const AppConfigContext = createContext<AppConfigContextValue | null>(null)

export function AppConfigProvider({ children }: { children: ReactNode }) {
  const [registrationEnabled, setRegistrationEnabled] = useState(false)
  const [onboardingTour, setOnboardingTour] = useState<OnboardingTourMode>('auto')
  const [isPending, setIsPending] = useState(true)

  useEffect(() => {
    let cancelled = false

    api
      .get<AppConfig>('/api/config')
      .then((data) => {
        if (cancelled) return
        setRegistrationEnabled(Boolean(data.registrationEnabled))
        try {
          setOnboardingTour(isOnboardingTourMode(data.onboardingTour))
        } catch {
          setOnboardingTour('auto')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRegistrationEnabled(true)
          setOnboardingTour('auto')
        }
      })
      .finally(() => {
        if (!cancelled) setIsPending(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<AppConfigContextValue>(
    () => ({ registrationEnabled, onboardingTour, isPending }),
    [registrationEnabled, onboardingTour, isPending],
  )

  return createElement(AppConfigContext.Provider, { value }, children)
}

export function useAppConfig() {
  const context = useContext(AppConfigContext)

  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider')
  }

  return context
}
